/**
 * PitchSection.tsx
 * ─────────────────
 * Section "Ce que fait SmartLawyer — Deux outils. Un seul abonnement."
 * Réutilisable sur toutes les pages publiques.
 *
 * Usage :
 *   <PitchSection />
 */

import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

// ── Dots couleur par IA ───────────────────────────────────────────────────────
const AI_CLIENTS = [
  { name: 'Claude',   dot: '#d97706' },
  { name: 'ChatGPT',  dot: '#10a37f' },
  { name: 'Gemini',   dot: '#4285f4' },
  { name: 'Copilot',  dot: '#0078d4' },
];

// ── 4 features bas de grille (textes : home.pitch.features.<id>.*) ─────────────
const FEATURES = [
  { id: 'semantic',   emoji: '⚡',  bg: 'bg-blue-50 dark:bg-blue-950/30' },
  { id: 'issue',      emoji: '✦',  bg: 'bg-violet-50 dark:bg-violet-950/30' },
  { id: 'graph',      emoji: '🔗', bg: 'bg-teal-50 dark:bg-teal-950/30' },
  { id: 'procedural', emoji: '🏛️', bg: 'bg-amber-50 dark:bg-amber-950/30' },
];

export const PitchSection = () => {
  const { t } = useTranslation();
  return (
  <section className="py-20 bg-background border-t border-border">
    <div className="max-w-[1100px] mx-auto px-6 sm:px-8">

      {/* ── Header ── */}
      <div className="mb-8">
        <span className="block text-[10px] font-bold uppercase tracking-[.14em] text-primary mb-2.5">
          {t('home.pitch.eyebrow')}
        </span>
        <h2 className="font-serif text-[30px] sm:text-[34px] font-bold text-foreground tracking-[-0.03em] leading-tight">
          {t('home.pitch.title_start')}{' '}
          <em className="not-italic text-primary">{t('home.pitch.title_highlight')}</em>
        </h2>
      </div>

      {/* ── Grille 2 colonnes ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Card 1 — Recherche — fond sombre */}
        <div className="relative rounded-2xl overflow-hidden p-7" style={{ background: '#1c2333' }}>
          {/* Déco watermark */}
          <span className="absolute right-[-12px] bottom-[-16px] text-[100px] opacity-[0.06] pointer-events-none select-none leading-none">
            ⚖
          </span>
          {/* Tag */}
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full bg-white/15 text-white border border-white/25 mb-4 tracking-wide">
            {t('home.pitch.search_tag')}
          </span>
          {/* Titre */}
          <h3 className="font-serif text-[18px] font-bold text-white leading-snug mb-2">
            <Trans i18nKey="home.pitch.search_title" components={{ br: <br /> }} />
          </h3>
          {/* Description */}
          <p className="text-[12px] text-white/55 leading-relaxed">
            <Trans
              i18nKey="home.pitch.search_desc"
              components={{ hl: <span className="text-white/85 font-medium" /> }}
            />
          </p>
        </div>

        {/* Card 2 — MCP — fond violet clair */}
        <div className="rounded-2xl border border-violet-200 dark:border-violet-800/60 bg-violet-50 dark:bg-violet-950/20 p-7">
          {/* Tag */}
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full bg-violet-600 text-white mb-4 tracking-wide">
            {t('home.pitch.mcp_tag')}
          </span>
          {/* Titre */}
          <h3 className="font-serif text-[18px] font-bold text-foreground leading-snug mb-2">
            <Trans i18nKey="home.pitch.mcp_title" components={{ br: <br /> }} />
          </h3>
          {/* Description */}
          <p className="text-[12px] text-muted-foreground leading-relaxed mb-4">
            <Trans
              i18nKey="home.pitch.mcp_desc"
              components={{ strong: <strong className="text-foreground font-semibold" /> }}
            />
          </p>
          {/* Logos IA */}
          <div className="flex flex-wrap gap-2">
            {AI_CLIENTS.map(({ name, dot }) => (
              <span
                key={name}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-white/75 dark:bg-background/60 text-foreground border border-violet-200 dark:border-violet-800/40"
              >
                <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: dot }} />
                {name}
              </span>
            ))}
            <span className="text-[11px] text-muted-foreground px-1 self-center">{t('home.pitch.others')}</span>
          </div>
        </div>

        {/* ── 4 features — span 2 colonnes ── */}
        <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {FEATURES.map(({ id, emoji, bg }) => (
            <div key={id} className="rounded-2xl border border-border bg-muted/30 p-4">
              <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center text-[15px] mb-2.5`}>
                {emoji}
              </div>
              <p className="text-[12px] font-semibold text-foreground mb-1">{t(`home.pitch.features.${id}.title`)}</p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">{t(`home.pitch.features.${id}.desc`)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA bas ── */}
      <div className="mt-10 pt-8 border-t border-border text-center">
        <p className="text-[12px] text-muted-foreground mb-3">
          {t('home.pitch.launch_offer')}
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-violet-600 text-white text-[14px] font-bold hover:bg-violet-700 transition-colors shadow-md shadow-violet-600/20"
        >
          {t('home.pitch.cta')}
        </Link>
        <p className="text-[11px] text-muted-foreground/60 mt-2">{t('home.pitch.no_commitment')}</p>
      </div>

    </div>
  </section>
  );
};